import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryZone } from '../types/delivery-zone.type';
import { ADMIN_DELIVERY_ZONES_API_PATHS } from '../constants/delivery-zones-api-paths.constants';

type UseAdminGetDeliveryZoneByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  deliveryZone: DeliveryZone | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryZoneByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  deliveryZone: null,
};

export function useAdminGetDeliveryZoneById({
  deliveryZoneId,
}: {
  deliveryZoneId: string;
}): UseAdminGetDeliveryZoneByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<DeliveryZone | undefined>(
        ADMIN_DELIVERY_ZONES_API_PATHS.GET_DELIVERY_ZONE_BY_ID(deliveryZoneId)
      );

      setResult({ isLoading: false, isRefetching: false, deliveryZone: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération de la zone de livraison'
        ),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [deliveryZoneId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [deliveryZoneId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
